import { useCallback } from 'react'
import { skipToken } from '@reduxjs/toolkit/query/react'
import { CLIENT_PERMISSION } from './clientAccess'
import { useGetMyClientAccessQuery, useGetMySubscriptionPermissionsQuery } from './clientUsersApi'

const EMPTY = []

/**
 * What the current user may do in the client that owns a subscription. While the permissions load
 * nothing is allowed, so buttons don't flash. If they can't be loaded (for example an older API), or
 * there is no subscription to check, everything is allowed: the API enforces access anyway and the
 * screen behaves as it did before roles existed.
 */
export const useSubscriptionAccess = (subscriptionId) => {
    const { currentData, isError } = useGetMySubscriptionPermissionsQuery(subscriptionId || skipToken)
    const isUnchecked = !subscriptionId || isError
    const permissions = currentData && currentData.permissions

    const can = useCallback((permission) => {
        if (isUnchecked) {
            return true
        }
        return Boolean(permissions) && permissions.includes(permission)
    }, [isUnchecked, permissions])

    return { can }
}

export const clientGrants = (client, permission) => Boolean(client.permissions) && client.permissions.includes(permission)

/** The clients the current user belongs to, with their role and permissions in each. */
export const useMyClients = () => {
    const { data, isLoading, isError } = useGetMyClientAccessQuery()
    return { clients: data || EMPTY, isLoading, isError }
}

/**
 * Whether the current user may create a connection. The API creates it in the user's first client. A
 * new account has no client until its first connection, so it can always start one.
 */
export const useConnectionCreateAccess = () => {
    const { clients, isLoading, isError } = useMyClients()
    if (isLoading) {
        return { canCreate: false, isLoading: true }
    }
    const client = clients[0]
    const canCreate = isError || !client || !client.permissions || clientGrants(client, CLIENT_PERMISSION.CONNECTION_CREATE)
    return { canCreate, isLoading: false }
}
